import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { LogLevel } from "@azure/msal-browser";
import {
  FluentProvider,
  webDarkTheme,
  webLightTheme,
} from "@fluentui/react-components";
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from "@azure/msal-browser";

import "./i18n";

/* global console, document, Office, module, process, require */

const rootElement: HTMLElement = document.getElementById("container");
const root = createRoot(rootElement);

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication({
  auth: {
    clientId: process.env.APP_ID,
    redirectUri:
      "https://" +
      process.env.STAGE +
      "-outlook-add-in.beneticsapi.com/consent.html",
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
});

// Default to using the first account if no account is active on page load
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  // TODO(databurro): Allow user to select which account they want to use.
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

// Optional - This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents();

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    msalInstance.setActiveAccount(account);
  }
});

function rgbStringToRgb(hex: string) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function isDark(bodyBackgroundColor: string) {
  var rgbVals = rgbStringToRgb(bodyBackgroundColor);
  if (rgbVals === null) {
    return false;
  }
  return rgbVals.r < 64 && rgbVals.g < 64 && rgbVals.b < 64;
}

/* Render application after Office initializes */
Office.onReady(async () => {
  root.render(
    <FluentProvider
      theme={
        isDark(Office.context.officeTheme.bodyBackgroundColor)
          ? webDarkTheme
          : webLightTheme
      }
    >
      <App
        msalInstance={msalInstance}
        languageCode={Office.context.displayLanguage}
      />
    </FluentProvider>
  );
});

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root.render(NextApp);
  });
}
